/**
 * TanStack Query hooks — Anomalies (lecture + résolution + réouverture).
 *
 * Routes API consommées (voir `apps/api/src/routes/anomalies.ts`) :
 *   GET    /v1/anomalies?submission_id=…   liste paginée
 *   PATCH  /v1/anomalies/:id/resolve       marque résolu (+ resolution_note?)
 *   PATCH  /v1/anomalies/:id/reopen        annule résolution
 *
 * Décision Chunk 9.D :
 *  - Pas de POST anomalies (création manuelle hors scope).
 *  - Pas d'expose de filtres serveur fins (resolved, severity, type) côté UI :
 *    on récupère TOUTES les anomalies de la submission et on filtre/trie
 *    client-side. Le volume reste petit (anomalies par submission ≪ 100 en
 *    pratique). Si la liste devient grosse, on basculera côté serveur.
 *
 * Invalidations onSuccess :
 *   - `anomalies.bySubmission(submissionId)` — la liste affichée
 *   - `submissions.detail(submissionId)` — par défense (le détail enrichi
 *     pourra exposer un compteur d'anomalies ouvertes plus tard).
 *
 * Type `AnomalyRow` typé localement (D26 — convention "shared seulement si
 * consommé ≥2 fois côté client/MCP"). À promouvoir vers shared si MCP
 * expose ces lignes.
 */

import type { DbPage } from "@kollectivia/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { api } from "../api";

import {
  sortAnomaliesCanonical,
  type AnomalyRow as AnomalyRowPure,
  type AnomalySeverity as AnomalySeverityPure,
} from "./anomalySort";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * Types `AnomalySeverity` + `AnomalyRow` extraits en 9.M.1 vers le
 * module pur `./anomalySort.ts` (sans dépendance API/Supabase/window) —
 * permet des tests Node sans DOM. Ré-exportés ici pour préserver l'API
 * publique consommée par les composants UI. Sémantique inchangée.
 */
export type AnomalySeverity = AnomalySeverityPure;
export type AnomalyRow = AnomalyRowPure;

export type AnomalyListParams = {
  submission_id?: string;
  cursor?: string | null;
  limit?: number;
};

// -----------------------------------------------------------------------------
// Query keys
// -----------------------------------------------------------------------------

export const anomaliesKeys = {
  all: ["anomalies"] as const,
  bySubmission: (submissionId: string) =>
    ["anomalies", "list", { submission_id: submissionId }] as const satisfies QueryKey,
};

// -----------------------------------------------------------------------------
// Builder
// -----------------------------------------------------------------------------

function buildListPath(params: AnomalyListParams): string {
  const sp = new URLSearchParams();
  if (params.submission_id) sp.set("submission_id", params.submission_id);
  if (params.cursor) sp.set("cursor", params.cursor);
  sp.set("limit", String(params.limit ?? 100));
  const qs = sp.toString();
  return qs ? `/v1/anomalies?${qs}` : "/v1/anomalies";
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export function useAnomaliesBySubmission(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionId
      ? anomaliesKeys.bySubmission(submissionId)
      : anomaliesKeys.bySubmission("__none__"),
    queryFn: () =>
      api.get<DbPage<AnomalyRow>>(
        buildListPath({ submission_id: submissionId as string, limit: 100 }),
      ),
    enabled: !!submissionId,
  });
}

// -----------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------

export type ResolveAnomalyVars = {
  anomalyId: string;
  submissionId: string;
  resolution_note?: string | null;
};

export function useResolveAnomaly() {
  const qc = useQueryClient();
  return useMutation<AnomalyRow, Error, ResolveAnomalyVars>({
    mutationFn: ({ anomalyId, resolution_note }) =>
      api.patch<AnomalyRow>(
        `/v1/anomalies/${anomalyId}/resolve`,
        resolution_note != null && resolution_note !== ""
          ? { resolution_note }
          : {},
      ),
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({
        queryKey: anomaliesKeys.bySubmission(vars.submissionId),
      });
      void qc.invalidateQueries({
        queryKey: ["submissions", "detail", vars.submissionId],
      });
    },
  });
}

export type ReopenAnomalyVars = {
  anomalyId: string;
  submissionId: string;
};

export function useReopenAnomaly() {
  const qc = useQueryClient();
  return useMutation<AnomalyRow, Error, ReopenAnomalyVars>({
    mutationFn: ({ anomalyId }) =>
      api.patch<AnomalyRow>(`/v1/anomalies/${anomalyId}/reopen`, {}),
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({
        queryKey: anomaliesKeys.bySubmission(vars.submissionId),
      });
      void qc.invalidateQueries({
        queryKey: ["submissions", "detail", vars.submissionId],
      });
    },
  });
}

// -----------------------------------------------------------------------------
// Tri canonique 9.D — extrait en 9.M.1 vers `./anomalySort.ts` (module pur
// testable Node sans DOM). Ré-exporté ici pour préserver l'API publique.
// Sémantique inchangée.
// -----------------------------------------------------------------------------

export { sortAnomaliesCanonical };
